if (!customElements.get('alexia-collection-scroll')) {
  customElements.define('alexia-collection-scroll', class extends HTMLElement {
    connectedCallback() {
      if (!('IntersectionObserver' in window)) return;
      this.grid = this.closest('#ProductGridContainer')?.querySelector('#product-grid');
      this.collection = this.closest('.collection');
      if (!this.grid || !this.collection) return;
      this.loaded = new Set();
      this.ownedModals = [];
      this.stale = false;
      this.busy = false;
      this.observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) this.loadNext();
      }, { rootMargin: '0px 0px 500px 0px' });
      this.filterObserver = new MutationObserver(() => {
        if (!this.collection.classList.contains('loading')) return;
        this.stale = true;
        this.controller?.abort();
        this.observer.disconnect();
        this.classList.remove('is-enhanced');
        this.querySelector('[data-scroll-status]').textContent = '';
        this.grid.removeAttribute('aria-busy');
      });
      this.filterObserver.observe(this.collection, { attributes: true, attributeFilter: ['class'] });
      this.classList.add('is-enhanced');
      this.observeNext();
    }

    disconnectedCallback() {
      this.stale = true;
      this.controller?.abort();
      this.observer?.disconnect();
      this.filterObserver?.disconnect();
      clearTimeout(this.timeout);
      this.ownedModals?.forEach((modal) => modal.remove());
    }

    observeNext() {
      this.observer.disconnect();
      if (this.querySelector('[data-scroll-next]')) this.observer.observe(this);
    }

    async loadNext() {
      const link = this.querySelector('[data-scroll-next]');
      if (this.busy || this.stale || !this.isConnected || !link || this.collection.classList.contains('loading')) return;
      const url = new URL(link.href, window.location.origin);
      if (url.origin !== window.location.origin || this.loaded.has(url.href)) return;
      const pageURL = url.href;
      const locationAtStart = window.location.href;
      url.searchParams.set('section_id', this.dataset.sectionId);
      this.busy = true;
      this.observer.disconnect();
      this.grid.setAttribute('aria-busy', 'true');
      this.querySelector('[data-scroll-status]').textContent = this.dataset.loading;
      this.controller = new AbortController();
      this.timeout = setTimeout(() => this.controller.abort(), 15000);
      try {
        const response = await fetch(url.href, { signal: this.controller.signal });
        if (!response.ok) throw new Error('Collection page unavailable');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        if (this.stale || !this.isConnected || this.collection.classList.contains('loading') || window.location.href !== locationAtStart) return;
        const incomingGrid = html.querySelector('#product-grid');
        const incomingPager = html.querySelector('alexia-collection-scroll');
        if (!incomingGrid || incomingGrid.dataset.id !== this.grid.dataset.id || !incomingPager) throw new Error('Invalid collection response');
        const cards = Array.from(incomingGrid.children);
        if (!cards.length) throw new Error('Empty collection response');
        const seen = new Set(Array.from(this.grid.children, (card) => card.dataset.collectionProductId));
        const fragment = document.createDocumentFragment();
        for (const card of cards) {
          const id = card.dataset.collectionProductId;
          if (!id) throw new Error('Missing product ID');
          if (seen.has(id)) continue;
          seen.add(id);
          const added = document.importNode(card, true);
          added.classList.add('scroll-trigger--cancel');
          added.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'));
          this.ownedModals.push(...added.querySelectorAll('quick-add-modal, quick-add-bulk-modal'));
          fragment.append(added);
        }
        this.grid.append(fragment);
        this.loaded.add(pageURL);
        this.querySelector('[data-scroll-pagination]').innerHTML = incomingPager.querySelector('[data-scroll-pagination]').innerHTML;
        this.querySelector('[data-scroll-status]').textContent = this.querySelector('[data-scroll-next]') ? this.dataset.loaded : this.dataset.end;
      } catch (error) {
        if (this.stale || !this.isConnected) return;
        this.classList.remove('is-enhanced');
        this.querySelector('[data-scroll-status]').textContent = this.dataset.error;
        this.failed = true;
      } finally {
        clearTimeout(this.timeout);
        this.busy = false;
        this.grid.removeAttribute('aria-busy');
        if (!this.failed && !this.stale && this.isConnected) this.observeNext();
      }
    }
  });
}
